import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import { SectorType } from 'src/app/interfaces/sector'

/** Tamanho máximo do atalho de um setor (#13788, RF02). */
export const ATALHO_MAX_LENGTH = 3

/** Janela sem tecla nova depois da qual a sequência em digitação é descartada (RN08). */
export const ATALHO_SEQUENCE_TIMEOUT_MS = 2000

/**
 * Normaliza o atalho conforme o operador digita: maiúsculas, só dígitos e letras,
 * nunca mais que ATALHO_MAX_LENGTH caracteres (RF02/RN06). `@`, espaço ou um 4º
 * caractere simplesmente não entram.
 */
export function normalizeAtalho(value: string | number | null | undefined): string {
  return String(value ?? '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .substring(0, ATALHO_MAX_LENGTH)
}

export type AtalhoConflictKind = 'duplicado' | 'prefixo'

export interface AtalhoConflict {
  kind: AtalhoConflictKind
  setor: SectorType
}

/**
 * Procura, entre os demais setores, um atalho igual (RN03) ou que seja início do
 * candidato / do qual o candidato seja início (RN04). O próprio registro em edição
 * (`ignoreId`) é ignorado (RN05). Retorna null quando o atalho está livre.
 */
export function findAtalhoConflict(
  atalho: string | null | undefined,
  setores: SectorType[],
  ignoreId?: number | null
): AtalhoConflict | null {
  const candidato = normalizeAtalho(atalho)
  if (!candidato) {
    return null
  }

  for (const setor of setores) {
    if (ignoreId !== undefined && ignoreId !== null && setor.id === ignoreId) {
      continue
    }
    const existente = normalizeAtalho(setor.atalho)
    if (!existente) {
      continue
    }
    if (existente === candidato) {
      return { kind: 'duplicado', setor }
    }
    if (existente.startsWith(candidato) || candidato.startsWith(existente)) {
      return { kind: 'prefixo', setor }
    }
  }
  return null
}

/**
 * Validador reativo do campo Atalho (RF03). Os setores e o id em edição são lidos
 * na hora da validação, porque a lista carrega depois do formulário ser criado.
 * Erros: `atalhoDuplicado` / `atalhoPrefixo`, ambos com `{ setor: <descricao> }`.
 */
export function atalhoValidator(
  getSetores: () => SectorType[],
  getOwnId: () => number | null | undefined
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const conflito = findAtalhoConflict(control.value, getSetores() || [], getOwnId())
    if (!conflito) {
      return null
    }
    const detalhe = { setor: conflito.setor.descricao || '', atalho: normalizeAtalho(conflito.setor.atalho) }
    return conflito.kind === 'duplicado' ? { atalhoDuplicado: detalhe } : { atalhoPrefixo: detalhe }
  }
}

/**
 * Traduz um evento de teclado no caractere que entra na sequência, ou null quando a
 * tecla deve ser ignorada (RN07): combinações com Ctrl/Alt/Meta, tecla mantida
 * pressionada (repeat) e qualquer tecla fora de [0-9A-Z]. `a` e `A` viram `A`; o bloco
 * numérico com NumLock entrega o mesmo `event.key` da linha numérica (RN06).
 */
export function keyToAtalhoChar(event: KeyboardEvent): string | null {
  if (event.ctrlKey || event.altKey || event.metaKey || event.repeat) {
    return null
  }
  const key = event.key
  if (typeof key !== 'string' || key.length !== 1) {
    return null
  }
  const char = key.toUpperCase()
  return /^[0-9A-Z]$/.test(char) ? char : null
}

/**
 * Sequência de teclas em digitação na tela de setores (RF07/RF08/RN08).
 *
 * A cada tecla, `push` devolve o setor cujo atalho ficou igual à sequência, ou null.
 * A sequência é descartada quando: passam mais de `timeoutMs` sem tecla nova; a
 * sequência deixa de ser início de algum atalho (a tecla errada descarta na hora e,
 * se ela própria for início de algum atalho, começa uma sequência nova); depois de
 * uma seleção; e em `reset()` ao sair da tela. Não há tecla de confirmação.
 */
export class AtalhoSequence {
  private buffer = ''
  private lastKeyAt = 0

  constructor(private readonly timeoutMs: number = ATALHO_SEQUENCE_TIMEOUT_MS) {}

  get current(): string {
    return this.buffer
  }

  reset(): void {
    this.buffer = ''
    this.lastKeyAt = 0
  }

  push(char: string, setores: SectorType[], now: number = Date.now()): SectorType | null {
    if (this.buffer && now - this.lastKeyAt > this.timeoutMs) {
      this.buffer = ''
    }
    this.lastKeyAt = now

    const candidatos = setores.filter((setor) => normalizeAtalho(setor.atalho) !== '')
    if (candidatos.length === 0) {
      this.buffer = ''
      return null
    }

    const tentativa = this.buffer + char
    const exato = candidatos.find((setor) => normalizeAtalho(setor.atalho) === tentativa)
    if (exato) {
      this.buffer = ''
      return exato
    }

    if (candidatos.some((setor) => normalizeAtalho(setor.atalho).startsWith(tentativa))) {
      this.buffer = tentativa
      return null
    }

    // Tecla errada: descarta a sequência na hora. A própria tecla pode abrir outra.
    this.buffer = ''
    if (tentativa !== char) {
      return this.push(char, setores, now)
    }
    return null
  }
}
